import React from 'react';
import { Eye, FileText } from 'lucide-react';

interface EmailTemplate {
  id: number;
  templateType: string;
  language: string;
  subject: string;
  htmlContent: string;
  textContent: string;
  availableVariables: string[];
  isActive: boolean;
  version: string;
}

interface TemplatePreviewProps {
  template: EmailTemplate;
  mode: 'html' | 'text';
  onModeChange: (mode: 'html' | 'text') => void;
}

export default function TemplatePreview({ 
  template, 
  mode,
  onModeChange 
}: TemplatePreviewProps) {
  const sampleVariables = {
    subscriber_email: 'user@example.com',
    settings_url: 'https://threatcompass.ee/settings/sample-token',
    unsubscribe_url: 'https://threatcompass.ee/unsubscribe/sample-token',
    verification_url: 'https://threatcompass.ee/verify/sample-token',
    risk_score: 8.5,
    articles: [
      {
        title: 'Sample Threat Article',
        summary: 'This is a sample threat article summary for preview purposes.',
        keyPoints: ['Key point 1', 'Key point 2', 'Key point 3'],
        riskScore: 8.5,
        categories: ['MILITARY', 'CYBER'],
        publishedAt: new Date().toISOString(),
        source: 'Sample News Source',
        url: 'https://example.com/article',
      },
    ],
    categories: ['MILITARY', 'CYBER'],
    date_range: 'January 15 - January 16, 2025',
    language: template.language,
    timezone: 'Europe/Tallinn',
    site_name: 'ThreatCompass',
    site_url: 'https://threatcompass.ee',
  };

  const renderTemplate = (content: string, variables: any) => {
    let rendered = content;
    
    // Simple template rendering for preview
    Object.keys(variables).forEach(key => {
      const value = variables[key];
      
      if (Array.isArray(value) && key === 'articles') {
        // Handle articles array
        const articlesHtml = value.map(article => {
          let articleBlock = mode === 'html' 
            ? `<div class="article">
                <h3>${article.title}</h3>
                <p><strong>Risk Score:</strong> ${article.riskScore}/10 | <strong>Categories:</strong> ${article.categories.join(', ')}</p>
                <p><strong>Summary:</strong> ${article.summary}</p>
                <ul>${article.keyPoints.map((point: string) => `<li>${point}</li>`).join('')}</ul>
                <p><strong>Source:</strong> ${article.source} | <a href="${article.url}">Read full article</a></p>
              </div>`
            : `---\n${article.title}\nRisk Score: ${article.riskScore}/10\nCategories: ${article.categories.join(', ')}\nSummary: ${article.summary}\n\nKey Points:\n${article.keyPoints.map((point: string) => `• ${point}`).join('\n')}\n\nSource: ${article.source}\nRead more: ${article.url}\n---`;
          return articleBlock;
        }).join('\n');
        
        rendered = rendered.replace(/\{\{#each articles\}\}[\s\S]*?\{\{\/each\}\}/g, articlesHtml);
      } else if (typeof value === 'string' || typeof value === 'number') {
        rendered = rendered.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), String(value));
      }
    });
    
    // Handle remaining template constructs
    rendered = rendered.replace(/\{\{#each.*?\}\}/g, '');
    rendered = rendered.replace(/\{\{\/each\}\}/g, '');
    rendered = rendered.replace(/\{\{this\}\}/g, 'item');
    
    return rendered;
  };

  const renderedContent = renderTemplate(
    mode === 'html' ? template.htmlContent : template.textContent,
    sampleVariables
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Template Preview</h3>
        <div className="flex gap-2">
          <button
            onClick={() => onModeChange('html')}
            className={`flex items-center gap-2 px-3 py-1 rounded-md ${
              mode === 'html'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
            }`}
          >
            <Eye size={16} />
            HTML
          </button>
          <button
            onClick={() => onModeChange('text')}
            className={`flex items-center gap-2 px-3 py-1 rounded-md ${
              mode === 'text'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
            }`}
          >
            <FileText size={16} />
            Text
          </button>
        </div>
      </div>

      <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-900 overflow-x-auto">
        {mode === 'html' ? (
          <div
            className="prose prose-sm max-w-none dark:prose-invert"
            dangerouslySetInnerHTML={{ __html: renderedContent }}
          />
        ) : (
          <pre className="whitespace-pre-wrap text-sm font-mono">
            {renderedContent}
          </pre>
        )}
      </div>

      <div className="text-sm text-gray-500 dark:text-gray-400">
        <p className="font-medium mb-1">Available Variables:</p>
        <div className="flex flex-wrap gap-2">
          {template.availableVariables.map(variable => (
            <code 
              key={variable}
              className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded text-xs"
            >
              {`{{${variable}}}`}
            </code>
          ))}
        </div>
      </div>
    </div>
  );
}